import { Link } from "react-router-dom";
import CATEGORY_SEO from "../data/categorySeo.json";
import { Sparkle, Moon, StarField, Wave } from "./Doodles";
import { OPEN_EVENT } from "../utils/analytics";
import "../styles/footer.css";

// Rendered on every page, so this is the link graph Google follows to reach the
// category and product pages. Keep every target a page that actually exists —
// the old /cart entries pointed at a disabled route and read as soft 404s.
const QUICK_LINKS = [
  { to: "/", label: "Начало" },
  { to: "/catalog", label: "Нашия магазин" },
  { to: "/categories", label: "Категории" },
  { to: "/blog", label: "Полезно" },
  { to: "/about", label: "За нас" },
  { to: "/privacy", label: "Политика за поверителност" },
];

const SOCIAL = [
  {
    href: "https://www.facebook.com/stams89",
    label: "Stamsy Art във Facebook",
    path: <path d="M14.5 8.5h2.5V5.2c-.5-.1-1.7-.2-2.9-.2-2.9 0-4.6 1.7-4.6 4.9v2.1H7v3.4h2.5V24h3.6v-8.6h2.6l.4-3.4h-3V9.6c0-.8.3-1.1 1.4-1.1Z" />,
  },
  {
    href: "https://www.tiktok.com/@stanimirarikova",
    label: "Stamsy Art в TikTok",
    path: <path d="M16.6 5.8A4.3 4.3 0 0 1 15.5 3h-3.1v12.4a2.6 2.6 0 1 1-2.6-2.6c.25 0 .5.04.73.1V9.8a5.7 5.7 0 1 0 4.97 5.65V9a7.4 7.4 0 0 0 4.3 1.38V7.3a4.3 4.3 0 0 1-3.2-1.5Z" />,
  },
];

const Icon = ({ path, size = 16 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
    fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
  >
    {path}
  </svg>
);

const ICON_MAIL = <><path d="M3 6.5h18v11H3z" /><path d="m3.5 7 8.5 6 8.5-6" /></>;
const ICON_PHONE = <path d="M6.5 3.5h3l1.5 4-2 1.5a10 10 0 0 0 6 6L16.5 13l4 1.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2Z" />;
const ICON_PIN = <><path d="M12 21s6.5-6 6.5-11a6.5 6.5 0 1 0-13 0C5.5 15 12 21 12 21Z" /><circle cx="12" cy="10" r="2.4" /></>;

export const Footer = () => (
  <footer className="sa-footer sa-on-dark">
    {/* Вълната вдига нощното небе над кремавата страница */}
    <Wave color="var(--sa-night)" />

    <div className="sa-footer-sky">
      <StarField count={22} />
      <Moon size={58} className="sa-doodle sa-footer-moon sa-float sa-float--slow" />

      <div className="sa-wrap sa-footer-grid">
        <div className="sa-footer-brand">
          <p className="sa-footer-logo">
            <span className="sa-footer-logo-name">
              Stamsy Art
              <Sparkle size={14} className="sa-footer-logo-star sa-twinkle" />
            </span>
            <span className="sa-footer-tag">рисувано с любов</span>
          </p>
          <p className="sa-footer-about">
            Магазин за ръчно рисувани тениски, подаръци и сувенири със снимки и картини по ваш избор.
          </p>

          <ul className="sa-footer-contact">
            <li>
              <Icon path={ICON_PIN} />
              Пазарджик, България — изработка по поръчка с доставка до Еконт и Спиди
            </li>
            <li>
              <Icon path={ICON_MAIL} />
              <a href="mailto:stamsy.art@abv.bg">stamsy.art@abv.bg</a>
            </li>
            <li>
              <Icon path={ICON_PHONE} />
              <a href="tel:+359893231708">+359 893 231 708</a>
            </li>
          </ul>

          <div className="sa-footer-social">
            {SOCIAL.map(({ href, label, path }) => (
              <a
                key={href}
                href={href}
                className="sa-footer-social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
                  {path}
                </svg>
              </a>
            ))}
          </div>
        </div>

        <nav className="sa-footer-col" aria-label="Бързи връзки">
          <p className="sa-footer-head">Бързи връзки</p>
          <ul className="sa-footer-list">
            {QUICK_LINKS.map(({ to, label }) => (
              <li key={to}>
                <Link to={to}>{label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="sa-footer-col" aria-label="Категории">
          <p className="sa-footer-head">Категории</p>
          <ul className="sa-footer-list sa-footer-list--cats">
            {Object.entries(CATEGORY_SEO).map(([slug, { h1 }]) => (
              <li key={slug}>
                <Link to={`/categories/${slug}`}>{h1}</Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className="sa-wrap sa-footer-base">
        <p className="sa-footer-copy">
          © {new Date().getFullYear()} Stamsy Art
          <button
            type="button"
            className="sa-footer-consent"
            onClick={() => window.dispatchEvent(new Event(OPEN_EVENT))}
          >
            Бисквитки
          </button>
        </p>
        {/* Пътят е абсолютен: относителният „img/…“ се разрешаваше спрямо
            текущия адрес, затова логата на плащанията ги нямаше навсякъде
            извън началната страница (/catalog/<id>/img/payments.png). */}
        <img className="sa-footer-pay" src="/img/payments.png" alt="" />
      </div>
    </div>
  </footer>
);
